(function () {
  'use strict';

  const SCROLL_REVEAL_RATIO = 0.15;
  const SCROLL_REVEAL_ROOT_MARGIN = '0px 0px -12% 0px';
  const SCROLL_REVEAL_MIN_VISIBLE = 0.18;
  const SCROLL_REVEAL_DURATION_MS = 1100;

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  class LuxeraTheme {
    constructor() {
      this.init();
    }

    init() {
      this.initHeader();
      this.initMobileMenu();
      this.initAnnouncementBar();
      this.initQuantitySelectors();
      this.initWishlist();
      this.initQuickAdd();
      this.initAnimations();
      this.initProductGallery();
      this.initProductTabs();
      this.initVariantPicker();
      this.initStickyATC();
      this.initSliders();
      this.initFilters();
      this.initFAQ();
      this.initFooterMenus();
      this.initProductForm();
    }

    setCartButtonState(button, state) {
      if (!button) return;
      button.classList.remove('is-cart-adding', 'is-cart-added');
      if (state === 'adding') {
        button.classList.add('is-cart-adding');
        button.setAttribute('aria-busy', 'true');
      } else if (state === 'added') {
        button.classList.add('is-cart-added');
        button.removeAttribute('aria-busy');
      } else {
        button.removeAttribute('aria-busy');
      }
    }

    async addToCartRequest({ button, formData, variantId, quantity = 1, onError }) {
      if (!window.themeSettings?.cartAddUrl) return false;
      if (button?.classList.contains('is-cart-adding')) return false;

      const wasDisabled = button?.disabled;
      this.setCartButtonState(button, 'adding');
      if (button) button.disabled = true;

      try {
        let response;
        if (formData) {
          response = await fetch(window.themeSettings.cartAddUrl, { method: 'POST', body: formData });
        } else {
          response = await fetch(window.themeSettings.cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity }] })
          });
        }

        if (response.ok) {
          this.setCartButtonState(button, 'added');
          document.dispatchEvent(new CustomEvent('cart:updated'));
          window.LuxeraCartDrawer?.open();
          window.setTimeout(() => {
            this.setCartButtonState(button, 'idle');
            if (button) button.disabled = wasDisabled;
          }, 1100);
          return true;
        }

        this.setCartButtonState(button, 'idle');
        if (button) button.disabled = wasDisabled;
        return false;
      } catch (error) {
        this.setCartButtonState(button, 'idle');
        if (button) button.disabled = wasDisabled;
        if (onError) onError(error);
        else throw error;
        return false;
      }
    }

    isCartAddSubmit(submitter, form) {
      if (submitter?.closest('.shopify-payment-button, .shopify-payment-button__button, [data-razorpay], .razorpay-payment-button, .magic-checkout')) {
        return false;
      }

      if (!submitter) {
        return !!form.querySelector('[data-add-to-cart]');
      }

      return submitter.matches('[data-add-to-cart]') || submitter.name === 'add';
    }

    initProductForm() {
      document.querySelectorAll('[data-product-form]').forEach((form) => {
        if (form.dataset.productFormInit === 'true') return;
        form.dataset.productFormInit = 'true';

        form.addEventListener('submit', async (e) => {
          if (!window.themeSettings?.cartAddUrl) return;
          if (!this.isCartAddSubmit(e.submitter, form)) return;

          e.preventDefault();
          const btn = form.querySelector('[data-add-to-cart]');
          const formData = new FormData(form);

          try {
            const added = await this.addToCartRequest({ button: btn, formData });
            if (!added) form.submit();
          } catch (err) {
            form.submit();
          }
        });
      });
    }

    initHeader() {
      const header = document.querySelector('.site-header');
      if (!header) return;

      const onScroll = () => {
        header.classList.toggle('is-scrolled', window.scrollY > 50);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    initMobileMenu() {
      const toggle = document.querySelector('[data-mobile-menu-toggle]');
      const menu = document.querySelector('[data-mobile-menu]');
      const overlay = document.querySelector('[data-mobile-menu-overlay]');
      const close = document.querySelector('[data-mobile-menu-close]');

      if (!toggle || !menu) return;

      const open = () => {
        menu.classList.add('is-open');
        overlay?.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      };

      const closeMenu = () => {
        menu.classList.remove('is-open');
        overlay?.classList.remove('is-open');
        document.body.style.overflow = '';
      };

      toggle.addEventListener('click', open);
      close?.addEventListener('click', closeMenu);
      overlay?.addEventListener('click', closeMenu);
    }

    initAnnouncementBar() {
      const bar = document.querySelector('[data-announcement-bar]');
      if (!bar) return;

      const slides = bar.querySelectorAll('[data-announcement-slide]');
      if (!slides.length) return;

      let current = 0;
      let timer = null;
      const autoplay = bar.dataset.autoplay === 'true';
      const speed = parseInt(bar.dataset.autoplaySpeed, 10) || 5000;
      const hasContentAnimation = bar.className.includes('announcement-bar--anim-');

      const activateSlide = (index, immediate) => {
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
          slide.classList.remove('is-content-ready');
        });

        if (!hasContentAnimation) {
          slides[index]?.classList.add('is-content-ready');
          return;
        }

        const reveal = () => slides[index]?.classList.add('is-content-ready');
        if (immediate) {
          requestAnimationFrame(reveal);
        } else {
          setTimeout(reveal, 80);
        }
      };

      const goTo = (index) => {
        const next = (index + slides.length) % slides.length;
        if (next === current) return;
        current = next;
        activateSlide(current, false);
      };

      const next = () => goTo(current + 1);
      const prev = () => goTo(current - 1);

      const startAutoplay = () => {
        if (!autoplay || slides.length <= 1) return;
        clearInterval(timer);
        timer = setInterval(next, speed);
      };

      const pauseAutoplay = () => {
        clearInterval(timer);
      };

      activateSlide(current, true);

      if (slides.length > 1) {
        startAutoplay();

        bar.querySelector('[data-announcement-prev]')?.addEventListener('click', () => {
          prev();
          pauseAutoplay();
          startAutoplay();
        });
        bar.querySelector('[data-announcement-next]')?.addEventListener('click', () => {
          next();
          pauseAutoplay();
          startAutoplay();
        });

        bar.addEventListener('mouseenter', pauseAutoplay);
        bar.addEventListener('mouseleave', startAutoplay);
      }
    }

    initQuantitySelectors() {
      document.querySelectorAll('[data-quantity-selector]').forEach((selector) => {
        const input = selector.querySelector('[data-quantity-input]');
        const minus = selector.querySelector('[data-quantity-minus]');
        const plus = selector.querySelector('[data-quantity-plus]');
        const min = parseInt(input.min, 10) || 1;
        const max = input.max ? parseInt(input.max, 10) : null;

        minus?.addEventListener('click', () => {
          const val = parseInt(input.value, 10) || min;
          if (val > min) input.value = val - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        plus?.addEventListener('click', () => {
          const val = parseInt(input.value, 10) || min;
          if (!max || val < max) input.value = val + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    initWishlist() {
      const updateButtons = () => {
        if (!window.LuxeraWishlist) return;
        document.querySelectorAll('.wishlist-btn').forEach((btn) => {
          const id = parseInt(btn.dataset.productId, 10);
          btn.classList.toggle('is-active', LuxeraWishlist.has(id));
        });
      };

      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.wishlist-btn');
        if (!btn || !window.LuxeraWishlist) return;
        e.preventDefault();
        const id = parseInt(btn.dataset.productId, 10);
        const handle = btn.dataset.productHandle;
        LuxeraWishlist.toggle(id, handle);
        updateButtons();
      });

      document.addEventListener('wishlist:updated', updateButtons);
      updateButtons();
    }

    initQuickAdd() {
      if (this.quickAddBound) return;
      this.quickAddBound = true;

      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('.quick-add-btn');
        if (!btn || btn.disabled || btn.classList.contains('is-cart-adding')) return;
        e.preventDefault();

        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        try {
          await this.addToCartRequest({ button: btn, variantId, quantity: 1 });
        } catch (err) {
          console.error(err);
        }
      });
    }

    isAnimationRoot(el) {
      const parentAnimated = el.parentElement?.closest('.scroll-animate, .fade-up, .fade-in');
      return !parentAnimated;
    }

    shouldRevealOnLoad(el) {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.height <= 0) return false;

      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, vh);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibleRatio = visibleHeight / rect.height;

      return visibleRatio >= SCROLL_REVEAL_MIN_VISIBLE && rect.top < vh * 0.88;
    }

    revealAnimated(el) {
      if (!el || el.classList.contains('is-visible')) return;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          el.classList.add('is-visible');
        });
      });
    }

    initAnimations(root = document) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        root.querySelectorAll('.scroll-animate, .fade-up, .fade-in, [data-slider-reveal]').forEach((el) => {
          el.classList.add('is-visible');
        });
        return;
      }

      const selector = [
        '.scroll-animate:not(.is-visible)',
        '.fade-up:not(.is-visible)',
        '.fade-in:not(.is-visible)',
        '[data-slider-reveal]:not(.is-visible)',
      ].join(', ');
      const elements = [...root.querySelectorAll(selector)].filter((el) => this.isAnimationRoot(el));
      if (!elements.length) return;

      if (!this.scrollAnimateObserver) {
        this.scrollAnimateObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting || entry.intersectionRatio < SCROLL_REVEAL_RATIO) return;
              this.revealAnimated(entry.target);
              this.scrollAnimateObserver.unobserve(entry.target);
            });
          },
          { threshold: [0, SCROLL_REVEAL_RATIO, 0.25, 0.4], rootMargin: SCROLL_REVEAL_ROOT_MARGIN }
        );
      }

      elements.forEach((el) => {
        if (el.dataset.scrollAnimateBound === 'true') return;
        el.dataset.scrollAnimateBound = 'true';

        if (this.shouldRevealOnLoad(el)) {
          this.revealAnimated(el);
          return;
        }

        this.scrollAnimateObserver.observe(el);
      });
    }

    initProductGallery() {
      /* Handled by product-gallery.js */
    }

    initProductTabs(root = document) {
      root.querySelectorAll('[data-product-tabs]').forEach((container) => {
        if (container.dataset.tabsInit === 'true') return;
        container.dataset.tabsInit = 'true';

        const tabs = container.querySelectorAll('[data-product-tab]');
        const panels = container.querySelectorAll('[data-product-panel]');

        tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            const target = tab.dataset.productTab;
            const nextPanel = container.querySelector(`[data-product-panel="${target}"]`);
            const currentPanel = container.querySelector('[data-product-panel].is-active');
            if (!nextPanel || nextPanel === currentPanel) return;

            tabs.forEach((t) => t.classList.remove('is-active'));
            tab.classList.add('is-active');

            if (currentPanel) {
              currentPanel.classList.add('is-leaving');
              currentPanel.classList.remove('is-active');
              window.setTimeout(() => {
                currentPanel.classList.remove('is-leaving');
                nextPanel.classList.add('is-active', 'is-entering');
                window.requestAnimationFrame(() => nextPanel.classList.remove('is-entering'));
              }, 180);
            } else {
              nextPanel.classList.add('is-active', 'is-entering');
              window.requestAnimationFrame(() => nextPanel.classList.remove('is-entering'));
            }
          });
        });
      });
    }

    initVariantPicker(root = document) {
      const pages =
        root === document
          ? document.querySelectorAll('[data-product-page]')
          : root.matches?.('[data-product-page]')
            ? [root]
            : root.querySelectorAll?.('[data-product-page]') || [];

      pages.forEach((productPage) => {
        const form = productPage.querySelector('[data-product-form]');
        if (!form) return;

        productPage.querySelectorAll('[data-variant-option]').forEach((option) => {
          if (option.dataset.variantBound === 'true') return;
          option.dataset.variantBound = 'true';
          option.addEventListener('click', () => {
            const group = option.closest('[data-variant-group]');
            group.querySelectorAll('[data-variant-option]').forEach((o) => o.classList.remove('is-selected'));
            option.classList.add('is-selected');
            const select = group.querySelector('[data-variant-select]');
            if (select) select.value = option.dataset.value;
            this.updateVariant(form, productPage);
          });
        });

        productPage.querySelectorAll('[data-variant-select]').forEach((select) => {
          if (select.dataset.variantBound === 'true') return;
          select.dataset.variantBound = 'true';
          select.addEventListener('change', () => {
            const group = select.closest('[data-variant-group]');
            group.querySelectorAll('[data-variant-option]').forEach((o) => {
              o.classList.toggle('is-selected', o.dataset.value === select.value);
            });
            this.updateVariant(form, productPage);
          });
        });
      });
    }

    getProductJson(form) {
      const scope = form.closest('.product-page') || document;
      return scope.querySelector('[data-product-json]');
    }

    getSelectedVariantOptions(scope) {
      const selectedOptions = [];
      scope.querySelectorAll('[data-variant-group]').forEach((group) => {
        const selected = group.querySelector('[data-variant-option].is-selected');
        if (selected) selectedOptions.push(selected.dataset.value);
      });
      return selectedOptions;
    }

    findMatchingVariant(product, selectedOptions) {
      return product.variants.find((variant) => {
        if (Array.isArray(variant.options) && variant.options.length) {
          return variant.options.every((option, index) => option === selectedOptions[index]);
        }
        return selectedOptions.every((option, index) => variant[`option${index + 1}`] === option);
      });
    }

    updateVariant(form, productPage = form.closest('[data-product-page]')) {
      const productData = this.getProductJson(form);
      if (!productData) return;

      const product = JSON.parse(productData.textContent);
      const selectedOptions = this.getSelectedVariantOptions(productPage || form);
      const variant = this.findMatchingVariant(product, selectedOptions);

      if (!variant) return;

      const idInput = form.querySelector('[name="id"]');
      if (idInput) idInput.value = variant.id;

      const priceEl = productPage?.querySelector('[data-product-price]');
      if (priceEl) {
        priceEl.textContent = this.formatMoney(variant.price);
      }

      const stickyPrice = document.querySelector('[data-sticky-price]') || document.querySelector('[data-sticky-atc] .sticky-atc__price');
      if (stickyPrice) {
        stickyPrice.textContent = this.formatMoney(variant.price);
      }

      const addBtn = form.querySelector('[data-add-to-cart]');
      if (addBtn) {
        addBtn.disabled = !variant.available;
        const label = variant.available ? addBtn.dataset.availableText : addBtn.dataset.soldOutText;
        const labelNode = addBtn.querySelector('.product-form__submit-label');
        if (labelNode) {
          labelNode.textContent = label;
        } else {
          addBtn.textContent = label;
        }
      }

      const compareEl = productPage?.querySelector('[data-product-compare]');
      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = this.formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      this.updateSaleBadges(productPage, variant);
    }

    updateSaleBadges(productPage, variant) {
      if (!productPage || !variant) return;

      const onSale = variant.compare_at_price > variant.price && variant.compare_at_price > 0;
      const percent = onSale
        ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)
        : 0;
      const template =
        productPage.dataset.saleLabelTemplate ||
        '[[percent]]% OFF';
      const label = template.replace('[[percent]]', String(percent));

      let badges = [...productPage.querySelectorAll('[data-product-sale-badge]')];

      if (onSale && !badges.length) {
        const priceRow = productPage.querySelector('.product-info__price-row');
        if (priceRow?.querySelector('[data-product-price]')) {
          const priceBadge = document.createElement('span');
          priceBadge.className = 'badge badge--sale product-info__sale-badge';
          priceBadge.dataset.productSaleBadge = '';
          priceBadge.dataset.saleLabelTemplate = template;
          const compareNode = priceRow.querySelector('[data-product-compare]');
          if (compareNode) {
            compareNode.insertAdjacentElement('afterend', priceBadge);
          } else {
            priceRow.querySelector('[data-product-price]')?.insertAdjacentElement('afterend', priceBadge);
          }
          badges.push(priceBadge);
        }

        const galleryBadges = productPage.querySelector('.product-gallery__badges');
        if (galleryBadges) {
          const galleryBadge = document.createElement('span');
          galleryBadge.className = 'badge badge--sale';
          galleryBadge.dataset.productSaleBadge = '';
          galleryBadge.dataset.saleLabelTemplate = template;
          galleryBadges.appendChild(galleryBadge);
          badges.push(galleryBadge);
        }
      }

      badges.forEach((badge) => {
        if (!onSale) {
          badge.remove();
          return;
        }
        badge.hidden = false;
        badge.textContent = label;
      });

      const priceRow = productPage.querySelector('.product-info__price-row');
      if (priceRow) {
        priceRow.classList.toggle('product-info__price-row--on-sale', onSale);
      }

      const priceEl = productPage.querySelector('[data-product-price]');
      if (priceEl) {
        priceEl.classList.toggle('product-info__price--on-sale', onSale);
      }
    }

    formatMoney(cents) {
      if (typeof window.themeFormatMoney === 'function') {
        return window.themeFormatMoney(cents);
      }

      const moneyFormat = window.themeSettings?.moneyFormat;
      if (moneyFormat && typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, moneyFormat);
      }

      return (cents / 100).toFixed(2);
    }

    initStickyATC() {
      const sticky = document.querySelector('[data-sticky-atc]');
      const productForm = document.querySelector('[data-product-form]');
      if (!sticky || !productForm) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          sticky.classList.toggle('is-visible', !entry.isIntersecting);
        },
        { threshold: 0 }
      );

      observer.observe(productForm.querySelector('[data-add-to-cart]') || productForm);

      document.querySelector('[data-sticky-atc-btn]')?.addEventListener('click', () => {
        productForm.querySelector('[data-add-to-cart]')?.click();
      });
    }

    isInView(el, margin = 0) {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.top < vh - margin && rect.bottom > margin;
    }

    setupSlider(slider) {
      if (slider.dataset.sliderInit === 'true') return;
      slider.dataset.sliderInit = 'true';

      const track = slider.querySelector('[data-slider-track]');
      const slideNodes = [...slider.querySelectorAll('[data-slider-slide]')];
      const prev = slider.querySelector('[data-slider-prev]');
      const next = slider.querySelector('[data-slider-next]');
      const dotsContainer = slider.querySelector('[data-slider-dots]');
      const mobileDotsOnly = slider.hasAttribute('data-slider-mobile-dots');
      if (!track || !slideNodes.length) return;

      let slideOffset = 0;
      let dotCount = 0;
      let resizeObserver = null;
      let isMouseDragging = false;
      let dragPointerId = null;
      let dragStartX = 0;
      let dragStartScroll = 0;
      let didDrag = false;
      let scrollTick = null;

      const getVisibleSlides = () => slideNodes.filter((slide) => slide.clientWidth > 0);

      const shouldShowDots = () => {
        if (!dotsContainer) return false;
        if (mobileDotsOnly && !window.matchMedia('(max-width: 749px)').matches) return false;
        return true;
      };

      const getPageCount = () => {
        if (!slideOffset) return 1;
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        if (maxScroll <= 2) return 1;
        return Math.round(maxScroll / slideOffset) + 1;
      };

      const updateDots = () => {
        if (!dotsContainer || dotsContainer.hidden || !shouldShowDots()) return;
        const active = slideOffset ? Math.round(track.scrollLeft / slideOffset) : 0;
        dotsContainer.querySelectorAll('[data-slider-dot]').forEach((dot, index) => {
          const isActive = index === active;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
      };

      const buildDots = () => {
        if (!dotsContainer) return;
        if (!shouldShowDots()) {
          dotsContainer.innerHTML = '';
          dotsContainer.hidden = true;
          dotCount = 0;
          return;
        }

        const pages = getPageCount();
        if (pages <= 1) {
          dotsContainer.innerHTML = '';
          dotsContainer.hidden = true;
          dotCount = 0;
          return;
        }

        if (pages === dotCount && dotsContainer.children.length === pages) {
          updateDots();
          return;
        }

        dotCount = pages;
        dotsContainer.hidden = false;
        dotsContainer.innerHTML = '';

        for (let index = 0; index < pages; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product-slider__dot';
          dot.dataset.sliderDot = String(index);
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', `Slide ${index + 1}`);
          dot.addEventListener('click', () => {
            if (!slideOffset) measure(false);
            track.scrollTo({ left: index * slideOffset, behavior: 'smooth' });
          });
          dotsContainer.appendChild(dot);
        }

        updateDots();
      };

      const updateButtons = () => {
        if (!prev || !next) return;
        const isAtStart = () => track.scrollLeft <= 2;
        const isAtEnd = () => {
          const visibleSlides = getVisibleSlides();
          if (!visibleSlides.length) return true;
          const lastSlide = visibleSlides[visibleSlides.length - 1];
          return lastSlide.offsetLeft + lastSlide.clientWidth <= track.scrollLeft + track.clientWidth + 2;
        };
        prev.toggleAttribute('disabled', isAtStart());
        next.toggleAttribute('disabled', isAtEnd());
      };

      const measure = (resetScroll = false) => {
        const visibleSlides = getVisibleSlides();
        if (visibleSlides.length >= 2) {
          slideOffset = visibleSlides[1].offsetLeft - visibleSlides[0].offsetLeft;
        } else if (visibleSlides.length === 1) {
          const gap = parseFloat(window.getComputedStyle(track).gap);
          slideOffset = visibleSlides[0].offsetWidth + (Number.isFinite(gap) ? gap : 24);
        } else {
          slideOffset = 0;
        }
        if (resetScroll) track.scrollLeft = 0;
        updateButtons();
        buildDots();
      };

      const stabilize = () => {
        measure(true);
        window.requestAnimationFrame(() => {
          measure(true);
          track.classList.add('is-stable');
          if (typeof ResizeObserver !== 'undefined' && !resizeObserver) {
            resizeObserver = new ResizeObserver(debounce(() => measure(false), 150));
            resizeObserver.observe(track);
          }
        });
      };

      const scrollByStep = (direction) => {
        if (!slideOffset) measure(false);
        if (!slideOffset) return;
        const delta = direction === 'next' ? slideOffset : -slideOffset;
        track.scrollTo({ left: track.scrollLeft + delta, behavior: 'smooth' });
      };

      const snapToNearest = () => {
        if (!slideOffset) return;
        const target = Math.round(track.scrollLeft / slideOffset) * slideOffset;
        track.scrollTo({ left: target, behavior: 'smooth' });
      };

      prev?.addEventListener('click', (event) => {
        event.preventDefault();
        scrollByStep('prev');
      });

      next?.addEventListener('click', (event) => {
        event.preventDefault();
        scrollByStep('next');
      });

      track.addEventListener(
        'scroll',
        () => {
          if (scrollTick) return;
          scrollTick = window.requestAnimationFrame(() => {
            scrollTick = null;
            updateButtons();
            updateDots();
          });
        },
        { passive: true }
      );

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('[data-slider-interactive], [data-video-reel-play], .video-reel-card__video, .fc-card, button, a, input, textarea, select, label')) return;
        isMouseDragging = true;
        didDrag = false;
        dragPointerId = event.pointerId;
        dragStartX = event.clientX;
        dragStartScroll = track.scrollLeft;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      });

      track.addEventListener('pointermove', (event) => {
        if (!isMouseDragging || event.pointerId !== dragPointerId) return;
        const delta = event.clientX - dragStartX;
        if (Math.abs(delta) > 4) didDrag = true;
        track.scrollLeft = dragStartScroll - delta;
      });

      const endMouseDrag = (event) => {
        if (!isMouseDragging || event.pointerId !== dragPointerId) return;
        isMouseDragging = false;
        dragPointerId = null;
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }
        if (didDrag) {
          slider.dataset.dragged = 'true';
          window.requestAnimationFrame(() => {
            delete slider.dataset.dragged;
          });
        }
        snapToNearest();
        updateButtons();
      };

      track.addEventListener('pointerup', endMouseDrag);
      track.addEventListener('pointercancel', endMouseDrag);

      slider.addEventListener(
        'click',
        (event) => {
          if (slider.dataset.dragged === 'true') {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );

      track.scrollLeft = 0;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(stabilize);
      });
    }

    isRevealSettled(el) {
      if (!el?.classList.contains('is-visible')) return false;
      const style = window.getComputedStyle(el);
      const opacityReady = parseFloat(style.opacity) >= 0.99;
      const transform = style.transform;
      const transformReady =
        !transform || transform === 'none' || transform === 'matrix(1, 0, 0, 1, 0, 0)';
      return opacityReady && transformReady;
    }

    waitForRevealParent(el, callback) {
      const parent = el.closest('.section-reveal');
      const isAnimated = parent?.classList.contains('scroll-animate');

      if (!isAnimated) {
        callback();
        return;
      }

      const run = () => window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
      const afterReveal = () => window.setTimeout(run, SCROLL_REVEAL_DURATION_MS);

      if (this.isRevealSettled(parent)) {
        run();
        return;
      }

      if (parent.classList.contains('is-visible')) {
        afterReveal();
        return;
      }

      const observer = new MutationObserver(() => {
        if (!parent.classList.contains('is-visible')) return;
        observer.disconnect();
        afterReveal();
      });
      observer.observe(parent, { attributes: true, attributeFilter: ['class'] });
    }

    initSliders(root = document) {
      root.querySelectorAll('[data-slider]').forEach((slider) => {
        if (slider.dataset.sliderInit === 'true' || slider.dataset.sliderDeferBound === 'true') return;
        slider.dataset.sliderDeferBound = 'true';

        const activate = () => this.waitForRevealParent(slider, () => this.setupSlider(slider));

        if (this.isInView(slider, 80)) {
          activate();
          return;
        }

        if (!('IntersectionObserver' in window)) {
          activate();
          return;
        }

        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0]?.isIntersecting) return;
            observer.disconnect();
            activate();
          },
          { threshold: 0.01, rootMargin: '120px 0px' }
        );
        observer.observe(slider);
      });
    }

    refreshSliders(root = document) {
      root.querySelectorAll('[data-slider]').forEach((slider) => {
        delete slider.dataset.sliderInit;
        delete slider.dataset.sliderDeferBound;
        slider.querySelector('[data-slider-track]')?.classList.remove('is-stable');
      });
      this.initSliders(root);
    }

    initFilters(root = document) {
      const filterToggle = root.querySelector('[data-filter-toggle]');
      const filterDrawer = root.querySelector('[data-filter-drawer]');
      const filterOverlay = root.querySelector('[data-filter-overlay]');
      const filterClose = root.querySelector('[data-filter-close]');

      filterToggle?.addEventListener('click', () => {
        filterDrawer?.classList.add('is-open');
        filterOverlay?.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });

      const closeFilter = () => {
        filterDrawer?.classList.remove('is-open');
        filterOverlay?.classList.remove('is-open');
        document.body.style.overflow = '';
      };

      filterClose?.addEventListener('click', closeFilter);
      filterOverlay?.addEventListener('click', closeFilter);

      root.querySelectorAll('[data-filter-url]').forEach((input) => {
        if (input.dataset.filterBound === 'true') return;
        input.dataset.filterBound = 'true';
        input.addEventListener('change', () => {
          const url = input.dataset.filterUrl;
          if (url) window.location.href = url;
        });
      });
    }

    initFAQ() {
      document.querySelectorAll('[data-faq-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const item = btn.closest('[data-faq-item]');
          const answer = item.querySelector('[data-faq-answer]');
          const isOpen = item.classList.contains('is-open');

          document.querySelectorAll('[data-faq-item].is-open').forEach((openItem) => {
            if (openItem !== item) {
              openItem.classList.remove('is-open');
              openItem.querySelector('[data-faq-answer]').hidden = true;
              const openBtn = openItem.querySelector('[data-faq-toggle]');
              if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
            }
          });

          item.classList.toggle('is-open', !isOpen);
          answer.hidden = isOpen;
          btn.setAttribute('aria-expanded', (!isOpen).toString());
        });
      });
    }

    initFooterMenus(root = document) {
      const footers = [...root.querySelectorAll('.site-footer--collapsible-menus')];
      if (!footers.length) return;

      const syncFooterMenus = () => {
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;
        footers.forEach((footer) => {
          footer.querySelectorAll('.site-footer__menu-details').forEach((details) => {
            if (isDesktop) {
              details.open = true;
            }
          });
        });
      };

      if (!this.footerMenusBound) {
        this.footerMenusBound = true;
        window.addEventListener('resize', debounce(syncFooterMenus, 150));
      }

      syncFooterMenus();
    }
  }

  const bootTheme = () => {
    if (!window.LuxeraTheme) {
      window.LuxeraTheme = new LuxeraTheme();
    }
    return window.LuxeraTheme;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootTheme);
  } else {
    bootTheme();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const theme = bootTheme();
    theme.initFilters(event.target);
    theme.initFooterMenus(event.target);
    theme.initVariantPicker(event.target);
    theme.initProductForm();
    theme.initProductTabs(event.target);
    theme.initAnimations(event.target);
    theme.refreshSliders(event.target);
    event.target.querySelectorAll?.('[data-video-reels]').forEach((section) => {
      delete section.dataset.videoReelsInit;
    });
    window.initVideoReels?.(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-slider]')?.forEach((slider) => {
      delete slider.dataset.sliderInit;
      delete slider.dataset.sliderDeferBound;
    });
    event.target.querySelectorAll?.('[data-video-reels]').forEach((section) => {
      delete section.dataset.videoReelsInit;
    });
  });

  document.addEventListener('shopify:section:reorder', () => {
    const theme = bootTheme();
    theme.refreshSliders(document);
    document.querySelectorAll('[data-video-reels]').forEach((section) => {
      delete section.dataset.videoReelsInit;
    });
    window.initVideoReels?.(document);
  });
})();
